import { useState, useCallback } from "react";
import JSZip from "jszip";
import { db } from "../config/firebase";
import { doc, collection, getDocs, getDoc } from "firebase/firestore";
import { stepConfigs } from "../_start/partials/widgets/forms/stepConfig";

interface StepFormData {
  form: Record<string, string>;
  stepProgress: number;
}

interface ExportData {
  projectTitle: string;
  projectDescription: string;
  projectID: string;
  exportedAt: string;
  overallScore?: number;
  categoryScores?: Record<string, number>;
  categoryReasons?: Record<string, string>;
  redFlags?: string[];
  strengths?: string[];
  recommendations?: string[];
  steps: Record<number, StepFormData>;
}

const STEP_TITLES: Record<number, string> = {
  1: "Market Segmentation",
  2: "Select a Beachhead Market",
  3: "Build an End User Profile",
  4: "Calculate TAM for Beachhead",
  5: "Profile the Persona",
  6: "Full Life Cycle Use Case",
  7: "High-Level Product Specification",
  8: "Quantify the Value Proposition",
  9: "Identify Next 10 Customers",
  10: "Define Your Core",
  11: "Chart Competitive Position",
  12: "Determine Customer's DMU",
  13: "Map Process to Acquire Customer",
  14: "Calculate TAM for Follow-on",
  15: "Design a Business Model",
  16: "Set Your Pricing Framework",
  17: "Calculate LTV",
  18: "Map the Sales Process",
  19: "Calculate COCA",
  20: "Identify Key Assumptions",
  21: "Test Key Assumptions",
  22: "Define the MVP",
  23: "Show Dogs Will Eat the Food",
  24: "Develop a Product Plan",
};

const CATEGORIES = [
  { label: "Market Opportunity", steps: [1, 2, 3, 4, 5] },
  { label: "Customer Understanding", steps: [6, 7, 8, 9, 10] },
  { label: "Value & Product", steps: [11, 12, 13, 14] },
  { label: "Business Model", steps: [15, 16, 17, 18, 19] },
  { label: "Validation & Execution", steps: [20, 21] },
  { label: "MVP & Product Plan", steps: [22, 23, 24] },
];

/**
 * Format step field values into readable text
 */
function formatStepFields(
  stepNum: number,
  formData: Record<string, string>
): string {
  const config = stepConfigs[stepNum];
  if (!config) return "";

  const lines: string[] = [];
  const allFields = config.sections.flatMap((s) => s.fields);

  allFields.forEach((field) => {
    const value = formData[field.name];
    if (value && value.trim()) {
      lines.push(`**${field.label}:** ${value.trim()}`);
    }
  });

  return lines.join("\n\n");
}

/**
 * Generate VALIDATION.md content (steps 1-21)
 */
function generateValidationMd(data: ExportData): string {
  const date = new Date(data.exportedAt).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const scoreLabel = data.overallScore !== undefined
    ? data.overallScore >= 70 ? "Strong" : data.overallScore >= 40 ? "Developing" : "Needs Work"
    : "Not scored";

  let md = `# VALIDATION.md — ${data.projectTitle}

> Generated by Business Foundry · ${date}

## Business Overview

**Project:** ${data.projectTitle}

**Description:** ${data.projectDescription || "No description provided."}

${data.overallScore !== undefined ? `**Validation Score:** ${data.overallScore}/100 (${scoreLabel})` : ""}

---

`;

  if (data.strengths?.length || data.redFlags?.length || data.recommendations?.length) {
    md += `## AI Assessment\n\n`;

    if (data.strengths?.length) {
      md += `### Strengths\n`;
      data.strengths.forEach((s) => { md += `- ${s}\n`; });
      md += "\n";
    }

    if (data.redFlags?.length) {
      md += `### Red Flags\n`;
      data.redFlags.forEach((f) => { md += `- ⚠️ ${f}\n`; });
      md += "\n";
    }

    if (data.recommendations?.length) {
      md += `### Recommendations\n`;
      data.recommendations.forEach((r, i) => { md += `${i + 1}. ${r}\n`; });
      md += "\n";
    }

    md += `---\n\n`;
  }

  // Steps 1-21 grouped by category
  const validationCategories = CATEGORIES.slice(0, 5); // First 5 categories = steps 1-21
  validationCategories.forEach((cat) => {
    md += `## ${cat.label}\n\n`;

    cat.steps.forEach((stepNum) => {
      const stepData = data.steps[stepNum];
      const title = STEP_TITLES[stepNum] || `Step ${stepNum}`;
      const completed = stepData && stepData.stepProgress > 0;

      md += `### Step ${stepNum}: ${title}\n\n`;

      if (completed && stepData) {
        const formatted = formatStepFields(stepNum, stepData.form);
        md += formatted || "_No details recorded._";
      } else {
        md += "_Not completed._";
      }

      md += "\n\n---\n\n";
    });
  });

  return md;
}

/**
 * Generate PRODUCT-SPEC.md content (steps 22-24)
 */
function generateProductSpecMd(data: ExportData): string {
  const date = new Date(data.exportedAt).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  let md = `# PRODUCT-SPEC.md — ${data.projectTitle}

> Generated by Business Foundry · ${date}
> This document defines the MVP and product plan. Feed into ARCHITECTURE.md and STORIES.md.

## Project

**Name:** ${data.projectTitle}

**Description:** ${data.projectDescription || "No description provided."}

---

`;

  const productSteps = [22, 23, 24];
  productSteps.forEach((stepNum) => {
    const stepData = data.steps[stepNum];
    const title = STEP_TITLES[stepNum] || `Step ${stepNum}`;
    const completed = stepData && stepData.stepProgress > 0;

    md += `## Step ${stepNum}: ${title}\n\n`;

    if (completed && stepData) {
      const formatted = formatStepFields(stepNum, stepData.form);
      md += formatted || "_No details recorded._";
    } else {
      md += "_Not completed — complete this step in Business Foundry before building._";
    }

    md += "\n\n---\n\n";
  });

  return md;
}

/**
 * Generate project-meta.json content
 */
function generateProjectMeta(data: ExportData): string {
  const meta = {
    projectName: data.projectTitle,
    projectDescription: data.projectDescription,
    projectID: data.projectID,
    exportedAt: data.exportedAt,
    exportedBy: "Business Foundry",
    validationStatus: "complete",
    scores: {
      overall: data.overallScore ?? null,
      ...(data.categoryScores || {}),
    },
    categoryReasons: data.categoryReasons ?? {},
    strengths: data.strengths ?? [],
    redFlags: data.redFlags ?? [],
    recommendations: data.recommendations ?? [],
    stepsCompleted: Object.values(data.steps).filter((s) => s.stepProgress > 0).length,
  };

  return JSON.stringify(meta, null, 2);
}

/**
 * Trigger a browser file download
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Shared helper: fetch project + step data from Firestore and build ExportData
 */
async function buildExportData(userID: string, projectID: string): Promise<{ data: ExportData; safeName: string } | null> {
  const userRef = doc(db, "users", userID);
  const projectRef = doc(userRef, "projects", projectID);

  const projectDoc = await getDoc(projectRef);
  if (!projectDoc.exists()) return null;
  const projectData = projectDoc.data();

  const progressRef = collection(projectRef, "progress");
  const progressSnapshot = await getDocs(progressRef);

  const steps: Record<number, StepFormData> = {};
  progressSnapshot.docs.forEach((d) => {
    const stepNum = parseInt(d.id.replace("step", ""), 10);
    if (!isNaN(stepNum)) {
      steps[stepNum] = {
        form: d.data().form || {},
        stepProgress: d.data().stepProgress || 0,
      };
    }
  });

  const businessScore = projectData?.businessScore;

  const data: ExportData = {
    projectTitle: projectData?.projectTitle || "Untitled Project",
    projectDescription: projectData?.projectDescription || "",
    projectID,
    exportedAt: new Date().toISOString(),
    overallScore: businessScore?.overall,
    categoryScores: businessScore?.categories,
    categoryReasons: businessScore?.categoryReasons,
    redFlags: businessScore?.redFlags,
    strengths: businessScore?.strengths,
    recommendations: businessScore?.recommendations,
    steps,
  };

  const safeName = data.projectTitle
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

  return { data, safeName };
}

/**
 * Hook for exporting the full Business Foundry validation package
 * for ingestion by the build pipeline.
 *
 * Generates three files:
 *   VALIDATION.md       — Steps 1-21 (business context)
 *   PRODUCT-SPEC.md     — Steps 22-24 (MVP definition)
 *   project-meta.json   — Metadata + scores
 */
/**
 * Generate NOTES.md content — one section per step that has non-empty notes
 */
function generateNotesMd(
  projectTitle: string,
  projectDescription: string,
  stepsWithNotes: { stepNum: number; title: string; notes: string }[]
): string {
  const date = new Date().toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  let md = `# Step Notes — ${projectTitle}\n\n`;
  md += `> Generated by Business Foundry · ${date}\n\n`;
  if (projectDescription) md += `**Project:** ${projectDescription}\n\n`;
  md += `---\n\n`;

  stepsWithNotes.forEach(({ stepNum, title, notes }) => {
    md += `## Step ${stepNum}: ${title}\n\n`;
    md += `${notes.trim()}\n\n`;
    md += `---\n\n`;
  });

  return md;
}

export const useValidationExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [isExportingNotes, setIsExportingNotes] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportBuildPackage = useCallback(
    async (userID: string, projectID: string): Promise<void> => {
      setIsExporting(true);
      setError(null);

      try {
        const result = await buildExportData(userID, projectID);
        if (!result) {
          setError("Project not found");
          return;
        }
        const { data, safeName } = result;

        // Download all three files
        downloadFile(generateValidationMd(data), `${safeName}-VALIDATION.md`, "text/markdown");
        await new Promise((resolve) => setTimeout(resolve, 300));
        downloadFile(generateProductSpecMd(data), `${safeName}-PRODUCT-SPEC.md`, "text/markdown");
        await new Promise((resolve) => setTimeout(resolve, 300));
        downloadFile(generateProjectMeta(data), `${safeName}-project-meta.json`, "application/json");
      } catch (err) {
        console.error("Validation export error:", err);
        setError("Failed to export build package. Please try again.");
      } finally {
        setIsExporting(false);
      }
    },
    []
  );

  /** Download all three export files bundled into a single ZIP */
  const exportZipPackage = useCallback(
    async (userID: string, projectID: string): Promise<void> => {
      setIsZipping(true);
      setError(null);

      try {
        const result = await buildExportData(userID, projectID);
        if (!result) {
          setError("Project not found");
          return;
        }
        const { data, safeName } = result;

        const zip = new JSZip();
        zip.file("VALIDATION.md", generateValidationMd(data));
        zip.file("PRODUCT-SPEC.md", generateProductSpecMd(data));
        zip.file("project-meta.json", generateProjectMeta(data));

        const blob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `${safeName}-export.zip`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("ZIP export error:", err);
        setError("Failed to generate ZIP. Please try again.");
      } finally {
        setIsZipping(false);
      }
    },
    []
  );

  /** Export all step notes as a single Markdown file */
  const exportNotesMarkdown = useCallback(
    async (userID: string, projectID: string): Promise<void> => {
      setIsExportingNotes(true);
      setError(null);

      try {
        const userRef = doc(db, "users", userID);
        const projectRef = doc(userRef, "projects", projectID);

        const projectDoc = await getDoc(projectRef);
        if (!projectDoc.exists()) {
          setError("Project not found");
          return;
        }
        const projectData = projectDoc.data();
        const projectTitle: string = (projectData?.projectTitle as string | undefined) ?? "Untitled Project";
        const projectDescription: string = (projectData?.projectDescription as string | undefined) ?? "";

        const progressRef = collection(projectRef, "progress");
        const progressSnapshot = await getDocs(progressRef);

        const stepsWithNotes: { stepNum: number; title: string; notes: string }[] = [];
        progressSnapshot.docs.forEach((d) => {
          const stepNum = parseInt(d.id.replace("step", ""), 10);
          if (isNaN(stepNum) || stepNum < 1 || stepNum > 24) return;
          const notes = d.data()?.notes as string | undefined;
          if (!notes || !notes.trim()) return;
          const title = STEP_TITLES[stepNum] ?? `Step ${stepNum}`;
          stepsWithNotes.push({ stepNum, title, notes: notes.trim() });
        });

        // Sort by step number
        stepsWithNotes.sort((a, b) => a.stepNum - b.stepNum);

        const safeName = projectTitle
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "");

        const content = generateNotesMd(projectTitle, projectDescription, stepsWithNotes);
        downloadFile(content, `${safeName}-notes.md`, "text/markdown");
      } catch (err) {
        console.error("Notes export error:", err);
        setError("Failed to export notes. Please try again.");
      } finally {
        setIsExportingNotes(false);
      }
    },
    []
  );

  return {
    exportBuildPackage,
    exportZipPackage,
    exportNotesMarkdown,
    isExporting,
    isZipping,
    isExportingNotes,
    error,
  };
};
